import { Division, Team, MetricType, ProgressStatus } from '../types'

export function getProgressPercentage(current: number, goal: number): number {
  if (goal <= 0) return 0
  return Math.round((current / goal) * 1000) / 10
}

export function getProgressColor(percentage: number): string {
  if (percentage >= 80) return '#10B981'
  if (percentage >= 50) return '#F59E0B'
  return '#EF4444'
}

export function getProgressBgColor(percentage: number): string {
  if (percentage >= 80) return 'bg-emerald-500'
  if (percentage >= 50) return 'bg-amber-500'
  return 'bg-red-500'
}

export function getProgressBgLightColor(percentage: number): string {
  if (percentage >= 80) return 'bg-emerald-50'
  if (percentage >= 50) return 'bg-amber-50'
  return 'bg-red-50'
}

export function getProgressTextColor(percentage: number): string {
  if (percentage >= 80) return 'text-emerald-700'
  if (percentage >= 50) return 'text-amber-700'
  return 'text-red-700'
}

export function getProgressBorderColor(percentage: number): string {
  if (percentage >= 80) return 'border-emerald-400'
  if (percentage >= 50) return 'border-amber-400'
  return 'border-red-400'
}

export function getProgressStatus(percentage: number): ProgressStatus {
  if (percentage >= 80) return 'on-track'
  if (percentage >= 50) return 'at-risk'
  return 'behind'
}

export function calculateProjection(
  current: number,
  daysElapsed: number,
  totalDays: number,
): number {
  if (daysElapsed <= 0) return 0
  const dailyRate = current / daysElapsed
  return Math.round(dailyRate * totalDays)
}

export function getProjectionLabel(projected: number, goal: number): string {
  if (goal <= 0) return 'No goal set'
  const projectedPercentage = Math.round((projected / goal) * 100)
  if (projectedPercentage >= 110) {
    return `On pace to exceed by ${projectedPercentage - 100}%`
  }
  if (projectedPercentage >= 95) {
    return `On track — projected ${projectedPercentage}%`
  }
  if (projectedPercentage >= 75) {
    return `At risk — projected ${projectedPercentage}% of goal`
  }
  return `Behind — projected ${projectedPercentage}% of goal`
}

export function aggregateTeamMetrics(
  team: Team,
  metric: MetricType,
): { current: number; goal: number } {
  let current = 0
  let goal = 0
  for (const lo of team.loanOfficers) {
    if (metric === 'softPull') {
      current += lo.softPullCount
      goal += lo.softPullGoal
    } else {
      current += lo.submittedCount
      goal += lo.submittedGoal
    }
  }
  return { current, goal }
}

export function aggregateDivisionMetrics(
  division: Division,
  metric: MetricType,
): { current: number; goal: number } {
  let current = 0
  let goal = 0
  for (const team of division.teams) {
    const teamMetrics = aggregateTeamMetrics(team, metric)
    current += teamMetrics.current
    goal += teamMetrics.goal
  }
  return { current, goal }
}

export function formatMetricLabel(metric: MetricType): string {
  return metric === 'softPull' ? 'Soft Pull' : 'Submitted'
}

export function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function formatDateRange(start: string, end: string): string {
  return `${formatDate(start)} – ${formatDate(end)}`
}
